// lazyWithRetry.js — React.lazy with a chunk-load retry + guarded reload.
//
// Why: a deploy can race a click — the browser holds an index.html that
// references chunk hashes which no longer exist on the server (or a stale
// service-worker/CDN cache serves old HTML) → import() fails and a plain
// React.lazy would hang the Suspense fallback forever. (This is exactly why
// code splitting was previously rolled back — see the note in Dashboard.js.)
//
// Strategy:
//   1. retry the import once after a short pause (transient failures)
//   2. if it still fails, reload the page at most once per minute to pick up
//      a fresh index.html; repeated failures bubble to the ErrorBoundary
//      instead of looping reloads.
import React from 'react';

const RELOAD_FLAG = 'lazy_chunk_reload_at';

const clearReloadFlag = () => {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
};

// Returns true at most once per minute so a genuinely broken deploy cannot
// put the tab into a reload loop.
const shouldReload = () => {
  try {
    const last = parseInt(sessionStorage.getItem(RELOAD_FLAG) || '0', 10);
    if (Date.now() - last < 60000) return false;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

export default function lazyWithRetry(importFactory, name = 'page') {
  return React.lazy(async () => {
    try {
      const mod = await importFactory();
      clearReloadFlag();
      return mod;
    } catch (err) {
      console.warn(`[lazyWithRetry] Chunk load failed for ${name}, retrying...`, err);
      await new Promise((resolve) => setTimeout(resolve, 700));
      try {
        const mod = await importFactory();
        clearReloadFlag();
        return mod;
      } catch (retryErr) {
        console.error(`[lazyWithRetry] Chunk load failed for ${name}:`, retryErr);
        if (shouldReload()) {
          window.location.reload();
        }
        // If the reload doesn't happen (e.g. offline), surface the error to
        // the nearest ErrorBoundary instead of hanging on the spinner.
        throw retryErr;
      }
    }
  });
}